import { APITypesV1 } from "@cur8/api-client";
import { Visit } from "@cur8/rich-entity";
import { useStartCheckInMutation } from "hooks/mutations/useStartCheckInMutation";
import { useUpdateCheckInSessionMutation } from "hooks/mutations/useUpdateCheckInSessionMutation";
import { useFetchSessionQuery } from "hooks/queries/useFetchSessionQuery";
import { useVisitToCheckInQuery } from "hooks/queries/useVisitToCheckInQuery";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo } from "react";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import {
  SeverityLevel,
  useAppInsights,
} from "render/context/AppInsightsContext";
import { paths } from "render/routes/paths";
import { WrongLocationView } from "render/views/SessionJoinCompleteView/components/WrongLocationView";
import { VisitStatus } from "render/views/SessionJoinCompleteView/visitStatus";
import { ConfirmedView } from "./components/ConfirmedView";
import { OutOfRangeView } from "./components/OutOfRangeView";
import { VisitNotFoundView } from "./components/VisitNotFoundView";

interface SessionJoinCompleteViewProps {
  goTo(url: string): void;
  onBack(): void;
  sessionId: string;
  siteId: string;
}

export function SessionJoinCompleteView({
  goTo,
  onBack,
  sessionId,
  siteId,
}: SessionJoinCompleteViewProps) {
  const appInsights = useAppInsights();
  const { data: session } = useFetchSessionQuery({
    sessionId,
    siteId: siteId,
  });

  const location = getAndParseLocationCookie();

  const startCheckInMutation = useStartCheckInMutation();
  const { data: featureFlags } = useFeaturePreference();

  const now = DateTime.now();

  const { visitToCheckInQuery, wasVisitAssignedOnBackend } =
    useVisitToCheckInQuery({
      checkInSession: session,
      origin: now,
    });

  const visit = visitToCheckInQuery.data;

  const { mutate: updateCheckInSessionMutation } =
    useUpdateCheckInSessionMutation();

  useEffect(() => {
    if (!visit) {
      return;
    }

    /**
     * 27537: Assign visit id to CheckinSession when a member joins the session
     *
     * This is a safety net for migration to the new behavior, when visit id
     * is assigned by APIBackend, when member joins the check-in session.
     *
     * In this scenario we don't want to re-assign the visit again in ConciergeUI,
     * so we do an early return if it's already assigned.
     *
     * Once changes in APIBackend are released, this entire effect will be removed.
     */
    if (session?.visitId) {
      if (wasVisitAssignedOnBackend) {
        appInsights.trackTrace({
          message: `A check-in session ${sessionId} already has an assigned visit, skipping manual visit assignment.`,
          severityLevel: SeverityLevel.Information,
        });
      }
      return;
    }

    appInsights.trackTrace({
      message: `A check-in session ${sessionId} has no assigned visitId after a member has joined the session. Assigning visit ${visit.visitId} on site ${visit.siteId} manually.`,
      severityLevel: SeverityLevel.Information,
    });

    return updateCheckInSessionMutation({
      sessionId,
      siteId: visit.siteId,
      visitId: visit.visitId,
    });
  }, [
    appInsights,
    session,
    sessionId,
    updateCheckInSessionMutation,
    visit,
    wasVisitAssignedOnBackend,
  ]);

  const isBodyScan = useMemo(() => {
    return visit?.medicalExams?.includes(APITypesV1.MedicalExam.NekoBodyScan);
  }, [visit?.medicalExams]);

  const url = useMemo(() => {
    if (visit) {
      return isBodyScan
        ? paths.awaitingConsent.url({
            patientId: visit.patientId,
            visitId: visit.visitId,
          })
        : featureFlags.newCheckInFlowEnabled
          ? paths.identityCheck.url({
              patientId: visit.patientId,
              visitId: visit.visitId,
            })
          : paths.assignToRoom.url({
              patientId: visit.patientId,
              visitId: visit.visitId,
            });
    }
  }, [isBodyScan, visit]);

  const handleContinue = useCallback(() => {
    if (url != null && visit != null) {
      if (visit.status === APITypesV1.VisitStatus.CheckedIn) {
        return goTo(url);
      }

      startCheckInMutation.mutate(
        { patientId: visit.patientId, visitId: visit.visitId },
        { onSuccess: () => goTo(url) }
      );
    }
  }, [goTo, startCheckInMutation, url, visit]);

  if (visitToCheckInQuery.status === "pending") {
    return;
  }

  if (!visit) {
    return <VisitNotFoundView />;
  }

  if (visit.siteId !== location?.siteId) {
    return <WrongLocationView visit={visit} />;
  }

  const status = validateVisitWithinBounds(visit);

  if (status === VisitStatus.Valid) {
    return (
      <ConfirmedView
        onContinue={handleContinue}
        isBusy={startCheckInMutation.isPending}
        onBack={onBack}
        visit={visit}
      />
    );
  }

  return (
    <OutOfRangeView
      status={status}
      visit={visit}
      onContinue={handleContinue}
      isBusy={startCheckInMutation.isPending}
    />
  );
}

const LATE_CHECKIN_MAX_DIFF_IN_MINUTES = 10;
const EARLY_CHECKIN_MAX_DIFF_IN_MINUTES = 10;

function validateVisitWithinBounds(visit: Visit): VisitStatus {
  const visitStart = visit.startTime;
  const diff = visitStart.diffNow("minutes").minutes;

  if (diff < -LATE_CHECKIN_MAX_DIFF_IN_MINUTES) {
    return VisitStatus.Late;
  }

  if (diff > EARLY_CHECKIN_MAX_DIFF_IN_MINUTES) {
    return VisitStatus.Early;
  }

  return VisitStatus.Valid;
}
